import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { memberService } from '../../services/memberService';
import toast from 'react-hot-toast';
import LoadingSpinner from '../Common/LoadingSpinner';

const MemberForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const isEditMode = !!id;

  const { register, handleSubmit, formState: { errors }, reset } = useForm();

  useEffect(() => {
    if (isEditMode) {
      loadMember();
    }
  }, [id]);

  const loadMember = async () => {
    try {
      const response = await memberService.getMemberById(id);
      reset(response.data);
    } catch (error) {
      toast.error('Failed to load member data');
      navigate('/members');
    }
  };

  const onSubmit = async (data) => {
    setLoading(true);
    try {
      if (isEditMode) {
        await memberService.updateMember(id, data);
        toast.success('Member updated successfully');
      } else {
        await memberService.createMember(data);
        toast.success('Member created successfully');
      }
      navigate('/members');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Operation failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto">
      <h1 className="text-2xl font-semibold text-gray-900 mb-6">
        {isEditMode ? 'Edit Member' : 'Add New Member'}
      </h1>

      <form onSubmit={handleSubmit(onSubmit)} className="card space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Full Name *
            </label>
            <input
              {...register('name', { required: 'Name is required' })}
              className="input-field"
              placeholder="Enter full name"
            />
            {errors.name && (
              <p className="mt-1 text-sm text-red-600">{errors.name.message}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Phone Number *
            </label>
            <input
              {...register('phone', {
                required: 'Phone number is required',
                pattern: {
                  value: /^01[3-9]\d{8}$/,
                  message: 'Invalid Bangladeshi phone number'
                }
              })}
              className="input-field"
              placeholder="01XXXXXXXXX"
            />
            {errors.phone && (
              <p className="mt-1 text-sm text-red-600">{errors.phone.message}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              NID Number
            </label>
            <input
              {...register('nid', {
                pattern: {
                  value: /^\d{10}$|^\d{17}$/,
                  message: 'NID must be 10 or 17 digits'
                }
              })}
              className="input-field"
              placeholder="Enter NID number"
            />
            {errors.nid && (
              <p className="mt-1 text-sm text-red-600">{errors.nid.message}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Email
            </label>
            <input
              {...register('email', {
                pattern: {
                  value: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
                  message: 'Invalid email address'
                }
              })}
              className="input-field"
              type="email"
              placeholder="example@email.com"
            />
            {errors.email && (
              <p className="mt-1 text-sm text-red-600">{errors.email.message}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Father's Name
            </label>
            <input
              {...register('father_name')}
              className="input-field"
              placeholder="Enter father's name"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Mother's Name
            </label>
            <input
              {...register('mother_name')}
              className="input-field"
              placeholder="Enter mother's name"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Occupation
            </label>
            <input
              {...register('occupation')}
              className="input-field"
              placeholder="Enter occupation"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Monthly Income
            </label>
            <input
              {...register('monthly_income', { valueAsNumber: true })}
              className="input-field"
              type="number"
              placeholder="0.00"
            />
          </div>

          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Present Address
            </label>
            <textarea
              {...register('present_address')}
              rows="3"
              className="input-field"
              placeholder="Enter present address"
            />
          </div>

          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Permanent Address
            </label>
            <textarea
              {...register('permanent_address')}
              rows="3"
              className="input-field"
              placeholder="Enter permanent address"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Nominee Name
            </label>
            <input
              {...register('nominee_name')}
              className="input-field"
              placeholder="Enter nominee name"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Nominee Relation
            </label>
            <input
              {...register('nominee_relation')}
              className="input-field"
              placeholder="e.g., Spouse, Child, Parent"
            />
          </div>
        </div>

        <div className="flex justify-end space-x-3">
          <button
            type="button"
            onClick={() => navigate('/members')}
            className="btn-secondary"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={loading}
            className="btn-primary flex items-center"
          >
            {loading && <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>}
            {isEditMode ? 'Update Member' : 'Create Member'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default MemberForm;